import React, { useState, useEffect, useMemo } from "react";
import api from "../utils/api";
import PlagiarismCheckPanel from "./PlagiarismCheckPanel";
import { getApprovalBlock } from "../utils/plagiarismPolicy";

export default function ResearchManagementModal({ isOpen, onClose, research, onResearchUpdated }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [action, setAction] = useState("");
  const [comments, setComments] = useState("");
  const [policy, setPolicy] = useState(null);
  const [liveResearch, setLiveResearch] = useState(research);

  useEffect(() => {
    setLiveResearch(research);
  }, [research]);

  useEffect(() => {
    if (!isOpen) return;
    api
      .get("/research/plagiarism-policy")
      .then(({ data }) => setPolicy(data))
      .catch(() => setPolicy(null));
  }, [isOpen]);

  const approvalBlock = useMemo(
    () => getApprovalBlock(liveResearch, policy),
    [liveResearch, policy]
  );

  const handleResearchRefreshed = () => {
    onResearchUpdated?.();
    if (!research?._id) return;
    api
      .get(`/research/${research._id}/plagiarism-status`)
      .then(({ data }) => {
        setLiveResearch((prev) => ({
          ...prev,
          plagiarismCheck: data.plagiarismCheck
        }));
        if (data.policy) setPolicy(data.policy);
      })
      .catch(() => {});
  };

  const handleAction = async (actionType) => {
    if (!research) return;

    if (actionType === "approved" && approvalBlock.blocked) {
      setError(approvalBlock.message);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const updateData = {
        status: actionType,
        adminComments: comments
      };

      await api.put(`/research/${research._id}/status`, updateData);

      onResearchUpdated();
      onClose();
      setAction("");
      setComments("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update research status");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await api.get(`/research/download/${research._id}`, {
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", research.pdfFilename || "research-document.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Failed to download document");
    }
  };

  if (!isOpen || !research) return null;

  const canApprove = research.status === "pending" && !approvalBlock.blocked;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 p-6 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">Research Management</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="font-semibold mb-2">Research Information</h3>
            <div className="space-y-2 text-sm">
              <p><strong>Title:</strong> {research.title}</p>
              <p><strong>Authors:</strong> {research.authors.join(", ")}</p>
              <p><strong>Year:</strong> {research.year}</p>
              <p><strong>Campus:</strong> {research.campus}</p>
              <p><strong>Department:</strong> {research.department}</p>
              <p><strong>Status:</strong>
                <span className={`ml-2 px-2 py-1 rounded text-xs ${
                  research.status === "approved" ? "bg-green-600" :
                  research.status === "pending" ? "bg-yellow-600" : "bg-red-600"
                }`}>
                  {research.status}
                </span>
              </p>
              <p><strong>Public Access:</strong> {research.isPublic ? "Yes" : "No"}</p>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-2">Abstract</h3>
            <p className="text-sm text-gray-300 mb-4">{research.abstract}</p>

            <h3 className="font-semibold mb-2">Keywords</h3>
            <div className="flex flex-wrap gap-1 mb-4">
              {research.keywords.map((keyword, index) => (
                <span key={index} className="bg-gray-700 px-2 py-1 rounded text-xs">
                  {keyword}
                </span>
              ))}
            </div>

            <h3 className="font-semibold mb-2">Upload Information</h3>
            <div className="space-y-1 text-sm">
              <p><strong>Uploaded by:</strong> {research.uploadedBy?.name}</p>
              <p><strong>Upload date:</strong> {new Date(research.createdAt).toLocaleDateString()}</p>
              <p><strong>File:</strong> {research.pdfFilename}</p>
            </div>
          </div>
        </div>

        <PlagiarismCheckPanel
          research={liveResearch}
          onUpdated={handleResearchRefreshed}
          policy={policy}
          approvalBlock={approvalBlock}
        />

        {research.adminComments && (
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Admin Comments</h3>
            <p className="text-sm text-gray-300 bg-gray-700 p-3 rounded">
              {research.adminComments}
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-4 mb-4">
          <button
            onClick={handleDownload}
            className="bg-blue-600 px-4 py-2 rounded hover:bg-blue-700"
          >
            Download PDF
          </button>

          {research.status === "pending" && (
            <>
              <button
                onClick={() => {
                  if (!canApprove) {
                    setError(approvalBlock.message || "Approval is blocked by plagiarism policy.");
                    return;
                  }
                  setAction("approve");
                  setError("");
                }}
                disabled={!canApprove}
                title={!canApprove ? approvalBlock.message : undefined}
                className="bg-green-600 px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Approve Research
              </button>
              <button
                onClick={() => setAction("reject")}
                className="bg-red-600 px-4 py-2 rounded hover:bg-red-700"
              >
                Reject Research
              </button>
            </>
          )}
        </div>

        {action && (
          <div className="mb-4">
            <h3 className="font-semibold mb-2">
              {action === "approve" ? "Approve" : "Reject"} Research
            </h3>
            {action === "approve" && approvalBlock.blocked && (
              <p className="text-red-400 text-sm mb-2">{approvalBlock.message}</p>
            )}
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              className="w-full p-2 rounded bg-gray-700 text-white h-24 mb-4"
              placeholder={`Add comments for ${action === "approve" ? "approval" : "rejection"}...`}
            />

            <div className="flex gap-2">
              <button
                onClick={() => handleAction(action === "approve" ? "approved" : "rejected")}
                disabled={loading || (action === "approve" && approvalBlock.blocked)}
                className={`px-4 py-2 rounded text-white disabled:opacity-50 ${
                  action === "approve" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {loading ? "Processing..." : `Confirm ${action === "approve" ? "Approval" : "Rejection"}`}
              </button>
              <button
                onClick={() => {
                  setAction("");
                  setComments("");
                }}
                className="bg-gray-600 px-4 py-2 rounded hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="text-red-400 text-sm mb-4">{error}</div>
        )}

        <button
          onClick={onClose}
          className="bg-gray-600 px-4 py-2 rounded hover:bg-gray-700"
        >
          Close
        </button>
      </div>
    </div>
  );
}
